import { CustomFieldDto, DataAggregationFunction, FilterType, SchemaTargetDto, SortDirection } from 'src/app/services/api.services';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { AppCurrency } from 'src/app/state/app/app.reducer';
import { InsightsQuerySyntax } from 'src/insightsQuery';
import { DataFormat, DataModel, DataSchema } from '../../dataModel';
import { DataType } from '../../dataModel/DataType';
import { encodeCacheKey } from '../../utils/cacheKey';
import { Predicate, PredicateType } from '../../utils/Predicate';
import { VisualCustomField } from '../VisualCustomField';
import { VisualField } from '../VisualField';
import { VisualFieldTarget } from '../VisualFieldTarget';
import { VisualFilter } from '../VisualFilter';

let countByDebugName: Record<string, number> = {};

function GetNullCacheKeyToken(type?: string) {
  return '\uffff\uffff' + type;
}

export class VisualDataRequest {
  readonly debugId: number;
  readonly debugName: string;

  readonly visualId: number | null;
  readonly schema: DataSchema;
  readonly fields: ReadonlyArray<VisualDataRequest.Field>;
  readonly filterGroups: ReadonlyArray<VisualFilter.PredicateGroup>;
  readonly customFields: ReadonlyArray<VisualDataRequest.CustomField>;
  readonly currency?: Readonly<AppCurrency>;
  readonly recordPlan?: boolean;

  constructor(public readonly dataModel: DataModel, init: VisualDataRequest.Init) {
    let debugName = init.debugName ?? '';
    this.debugId = countByDebugName[debugName] ??= 1;
    countByDebugName[debugName] = this.debugId + 1;

    this.debugName = `#${('' + this.debugId).padStart(3, '0')} ${debugName}`;


    this.visualId = init.visualId;
    this.schema = init.schema;
    this.fields = init.fields.map(f => (<VisualDataRequest.Field>{
      name: f.visualFieldId,
      target: f.target,
      sorting: (init.dontApplyFieldSorting || !f.sorted()) ? null : {
        sortDirection: f.sortDirection(),
        sortOrder: f.sortOrder()
      }
    }));

    this.filterGroups = init.filterGroups.map(g => ({
      operator: g.operator,
      filters:
        g.filters.filter(f => f instanceof VisualFilter ? (!f.disabled() && f.isValid()) : true)
          .map(f => f instanceof VisualFilter ? f.predicate() : f)
          .filter(VisualFilter.Predicate.isComplete) ?? []
    }));

    this.customFields = init.customFields?.map(mapCustomField) ?? [];

    this.currency = init.currency;
    this.recordPlan = init.recordPlan;

    function mapCustomField(cf: VisualCustomField) {
      return {
        dataFormat: cf.dataFormat(),
        dataType: cf.dataType(),
        expr: cf.expr()?.syntax,
        sourceText: cf.sourceText(),
        name: cf.name(),
        schema: cf.context.schema.getSchemaTargetDto(),
        children: cf.children()?.map(mapCustomField),
        dto: cf.createCustomFieldDto(),
      };
    }
  }


  private _cacheKey: string;
  get cacheKey(): string {
    return this._cacheKey ??= this.computeCacheKey();
  }

  private computeCacheKey(): string {
    let customFieldMap = new Map<string, VisualDataRequest.CustomField>(this.customFields.map(cf => [cf.name.toLowerCase(), cf]));
    let referencedCustomFields = new Set<VisualDataRequest.CustomField>();
    let referencedTargets = this.getReferencedTargets();

    let parts: any[] = [];

    // TODO: Luke - check why this is needed
    // parts.push(this.visualId ?? 0);

    parts.push(normalizeSchema(this.schema.getSchemaTargetDto()));

    parts.push(this.fields
      .map(normalizeField)
      .sort((a, b) => a.localeCompare(b)));

    parts.push(this.filterGroups
      .filter(x => x.filters?.length)
      .map(g => ([g.operator, g.filters
          .filter(f => !(
            // Ignore basic filters 'All' where fields are already included in the visual
            // The filter only does something when the field is not already referenced
            f.type == PredicateType.Basic &&
            f.operator == Predicate.BasicOperator.All &&
            referencedTargets.some(x => x.equals(f.target)))
          )
          .map(normalizeFilter)
          .sort((a, b) => a.localeCompare(b))
      ])));

    parts.push(this.currency?.currencyCode?.toLowerCase() ?? 'target');

    parts.push([...referencedCustomFields.values()]
      .map(normalizeCustomField)
      .sort((a, b) => a.localeCompare(b)));

    parts.push(this.recordPlan);

    return encodeCacheKey(JSON.stringify(parts));


    function normalizeField(f: VisualDataRequest.Field, i: number) {
      return [
        normalizeTarget(f.target),
        f.sorting ? (f.sorting.sortOrder ?? 10000 + i) : GetNullCacheKeyToken('sortOrder'),
        f.sorting ? (f.sorting.sortDirection ?? SortDirection.Ascending) : GetNullCacheKeyToken('sortDirection')
      ].join('\n');
    }

    function normalizeFilter(predicate: VisualFilter.Predicate) {
      if (!predicate)
        return '\n';

      if (predicate.type === PredicateType.Advanced) {
        return [
          FilterType.Advanced,
          normalizeTarget(predicate.target),
          predicate.logicalOperator,
          normalizeValue(predicate.conditions?.map(x => [x.type, x.value]) ?? [])
        ].join('\n');
      }
      else if (predicate.type === PredicateType.Basic) {
        return [
          FilterType.Basic,
          normalizeTarget(predicate.target),
          predicate.operator,
          normalizeValue(predicate.values ?? [])
        ].join('\n');
      }
      else if (predicate.type === PredicateType.Dynamic) {
        return [
          FilterType.Dynamic,
          normalizeTarget(predicate.target),
          predicate.kind,
        ].join('\n');
      }
      else if (predicate.type === PredicateType.RelativeDate) {
        return [
          FilterType.RelativeDate,
          normalizeTarget(predicate.target),
          predicate.operator,
          predicate.firstUnitCount,
          predicate.firstUnitType,
          predicate.secondUnitCount,
          predicate.secondUnitType,
          predicate.includeToday
        ].join('\n');
      }
      else if (predicate.type === PredicateType.RelativeTime) {
        return [
          FilterType.RelativeTime,
          normalizeTarget(predicate.target),
          predicate.operator,
          predicate.firstUnitCount,
          predicate.firstUnitType,
          predicate.secondUnitCount,
          predicate.secondUnitType,
        ].join('\n');
      }
      else if (predicate.type === PredicateType.TopN) {
        return [
          FilterType.TopN,
          normalizeTarget(predicate.target),
          predicate.operator,
          predicate.maxCount
        ].join('\n');
      }

      return JSON.stringify(VisualFilter.Predicate.createFilterDto(predicate));
    }

    function normalizeCustomField(cf: VisualDataRequest.CustomField) {
      return [
        normalizeSchema(cf.schema),
        cf.name?.toLowerCase() ?? GetNullCacheKeyToken('cf.name'),
        cf.expr?.toString(true, false) ?? cf.sourceText ?? GetNullCacheKeyToken('cf.expr'),
        normalizeValue(cf.dataType?.value ?? DataType.Text.value),
        normalizeValue(cf.dataFormat ?? {}),
        cf.children.map(normalizeCustomField)
      ].join('\n');
    }

    function normalizeTarget(target: VisualFieldTarget) {
      if (!target)
        return GetNullCacheKeyToken('target');

      let dto = target.schemaFieldTargetDto;

      if (dto.group.toLowerCase() == VisualCustomField.GROUP_NAME.toLowerCase()) {
        let customField = customFieldMap.get(dto.field.toLowerCase());
        if (customField)
          referencedCustomFields.add(customField);
      }

      return [
        normalizeSchema(dto.schema),
        dto.group?.toLowerCase(),
        dto.field?.toLowerCase(),
        dto.childField?.toLowerCase(),
        dto.aggregation ?? DataAggregationFunction.DoNotSummarize
      ].join('\n');
    }

    function normalizeSchema(schema: SchemaTargetDto) {
      if (!schema)
        return GetNullCacheKeyToken('schema');

      return SchemaUtils.getTargetString(schema)?.toLowerCase();
    }

    function normalizeValue(value: any) {
      if (isNullOrUndefined(value))
        return GetNullCacheKeyToken('value');

      if (typeof value === 'object') {
        return Object.entries(value).map(x => [x[0], normalizeValue(x[1])]);
      }

      if (Array.isArray(value)) {
        return value.map(normalizeValue);
      }

      return value;
    }
  }

  getReferencedTargets() {
    let referencedTargets: VisualFieldTarget[] = [];

    for (let t of this.fields.map(x => x.target).filter(t => t)) {
      if (!referencedTargets.some(x => x.equals(t)))
        referencedTargets.push(t);
    }

    for (let t of this.filterGroups.flatMap(x => x.filters.map(y => y.target)).filter(t => t)) {
      if (!referencedTargets.some(x => x.equals(t)))
        referencedTargets.push(t);
    }

    return referencedTargets;
  }


  _isValid = null;
  get isValid() {
    return this._isValid ??= this.computeIsValid();
  }

  private computeIsValid() {
    let targets = this.fields.map(x => x.target).concat(this.filterGroups.flatMap(x => x.filters.map(y => y.target)));
    if (targets.some(x => !x?.isValid))
      return false;

    return true;
  }
}

export namespace VisualDataRequest {
  export interface Field {
    /** Unique name of this field, for reference purposes. Has no effect on queries. */
    readonly name: string;

    readonly target: VisualFieldTarget;
    readonly sorting?: FieldSorting;
  }

  export interface FieldSorting {
    readonly sortOrder?: number;
    readonly sortDirection?: SortDirection;
  }

  export interface CustomField {
    readonly schema: SchemaTargetDto;
    readonly name: string;
    readonly expr: InsightsQuerySyntax | null;
    readonly sourceText: string;
    readonly dataType: DataType;
    readonly dataFormat: DataFormat;
    readonly children: ReadonlyArray<CustomField>;
    readonly dto: CustomFieldDto;
  }

  export interface Init {
    readonly debugName?: string;
    readonly visualId?: number | null;
    readonly schema: DataSchema;
    readonly fields: ReadonlyArray<VisualField>;
    readonly filterGroups: ReadonlyArray<VisualFilter.PredicateGroup>;
    readonly customFields: ReadonlyArray<VisualCustomField>;
    readonly currency?: Readonly<AppCurrency>;
    readonly dontApplyFieldSorting?: boolean;
    readonly recordPlan: boolean;
  }
}
